import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchCustomerHistory,
  createWholesaleSale,
  createWholesalePayment,
  updateWholesaleCustomer,
  deleteWholesaleCustomer,
  selectCustomerHistory,
  selectCustomerHistoryStatus,
  selectCustomerHistoryError,
  clearCustomerHistory,
} from "../features/wholesale/wholesaleSlice";
import { fetchStores, selectStores, selectStoresStatus } from "../features/onlineSeller/onlineSellerSlice";
import { selectAuth } from "../features/auth/authSlice";
import { can } from "../features/auth/permissions";

export default function WholesaleCustomerDetailPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { customerId } = useParams();

  const history = useSelector(selectCustomerHistory);
  const histStatus = useSelector(selectCustomerHistoryStatus);
  const histError = useSelector(selectCustomerHistoryError);
  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);
  const auth = useSelector(selectAuth);
  const canDeleteCustomer = can(auth.user, "wholesale_customer", "delete");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [newSaleModal, setNewSaleModal] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [saleNotes, setSaleNotes] = useState("");
  const [saleLoading, setSaleLoading] = useState(false);
  const [saleError, setSaleError] = useState(null);

  const [paymentModal, setPaymentModal] = useState(null); // sale object
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentType, setPaymentType] = useState("cash");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState("");
  const [discountSaving, setDiscountSaving] = useState(false);
  const [discountError, setDiscountError] = useState(null);
  const [discountOk, setDiscountOk] = useState(false);

  useEffect(() => {
    dispatch(clearCustomerHistory());
    dispatch(fetchCustomerHistory(customerId));
  }, [dispatch, customerId]);

  const customer = history?.customer;
  const sales = history?.sales || [];
  const payments = history?.payments || [];
  const totalDebt = Number(customer?.total_debt) || 0;
  const prepaymentBalance = Number(customer?.prepayment_balance) || 0;

  useEffect(() => {
    if (!customer) return;
    setDiscountType(customer.default_discount_type || "none");
    setDiscountValue(
      customer.default_discount_type && customer.default_discount_type !== "none"
        ? String(customer.default_discount_value ?? "")
        : ""
    );
    setDiscountError(null);
    setDiscountOk(false);
  }, [
    customer?.id,
    customer?.default_discount_type,
    customer?.default_discount_value,
  ]);

  const handleSaveDiscount = async () => {
    setDiscountError(null);
    setDiscountOk(false);
    if (discountType === "percent" || discountType === "amount") {
      const v = String(discountValue ?? "").trim();
      if (v === "" || Number.isNaN(Number(v)) || Number(v) < 0) {
        setDiscountError("Укажите значение скидки (число ≥ 0)");
        return;
      }
      if (discountType === "percent" && Number(v) > 100) {
        setDiscountError("Процент не может быть больше 100");
        return;
      }
    }
    setDiscountSaving(true);
    try {
      const val = discountType === "none" ? "0" : String(discountValue).trim();
      await dispatch(
        updateWholesaleCustomer({
          customerId: Number(customerId),
          default_discount_type: discountType,
          default_discount_value: val,
        })
      ).unwrap();
      setDiscountOk(true);
      dispatch(fetchCustomerHistory(customerId));
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.detail || "Ошибка сохранения";
      setDiscountError(msg);
    } finally {
      setDiscountSaving(false);
    }
  };

  const salesWithDebt = useMemo(() => {
    return sales
      .filter((s) => s.status === "confirmed" && (Number(s.debt_amount) || 0) > 0)
      .sort((a, b) => {
        const ta = a.sale_dt ? new Date(a.sale_dt).getTime() : 0;
        const tb = b.sale_dt ? new Date(b.sale_dt).getTime() : 0;
        return ta - tb;
      });
  }, [sales]);

  const handleOpenNewSale = () => {
    if (storesStatus === "idle") dispatch(fetchStores());
    setSaleError(null);
    setSelectedStoreId("");
    setSaleNotes("");
    setNewSaleModal(true);
  };

  const handleCreateSale = async () => {
    if (!selectedStoreId) {
      setSaleError("Выберите склад");
      return;
    }
    setSaleLoading(true);
    setSaleError(null);
    try {
      const sale = await dispatch(
        createWholesaleSale({
          customer_id: Number(customerId),
          store_id: Number(selectedStoreId),
          notes: saleNotes,
        })
      ).unwrap();
      setNewSaleModal(false);
      navigate(`/wholesale/sales/${sale.id}`);
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.detail || "Ошибка создания продажи";
      setSaleError(msg);
    } finally {
      setSaleLoading(false);
    }
  };

  const handleOpenPayment = (sale, { prefillAmount } = {}) => {
    setPaymentModal(sale);
    setPaymentAmount(
      prefillAmount != null && prefillAmount !== "" ? String(prefillAmount) : ""
    );
    setPaymentType("cash");
    setPaymentNotes("");
    setPaymentError(null);
  };

  const handleDeleteCustomer = async () => {
    if (!customer) return;
    if (!window.confirm(`Удалить клиента «${customer.name}»? История продаж и долгов сохранится.`)) return;
    setDeleteLoading(true);
    try {
      await dispatch(deleteWholesaleCustomer(customer.id)).unwrap();
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.detail || "Ошибка удаления клиента";
      window.alert(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenPayDebtFromHeader = () => {
    if (!salesWithDebt.length) {
      setPaymentError(null);
      window.alert("Нет подтверждённых продаж с непогашенным долгом.");
      return;
    }
    const sale = salesWithDebt[0];
    const debt = Number(sale.debt_amount) || 0;
    handleOpenPayment(sale, { prefillAmount: debt > 0 ? debt.toFixed(2) : "" });
  };

  const handleCreatePayment = async () => {
    if (!paymentAmount || Number(paymentAmount) <= 0) {
      setPaymentError("Введите сумму оплаты");
      return;
    }
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      await dispatch(
        createWholesalePayment({
          sale_id: paymentModal.id,
          customer_id: Number(customerId),
          amount: paymentAmount,
          payment_type: paymentType,
          notes: paymentNotes,
        })
      ).unwrap();
      setPaymentModal(null);
      dispatch(fetchCustomerHistory(customerId));
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.detail || "Ошибка платежа";
      setPaymentError(msg);
    } finally {
      setPaymentLoading(false);
    }
  };

  const statusLabel = {
    draft: { text: "Черновик", color: "var(--c-text-muted)" },
    confirmed: { text: "Подтверждена", color: "var(--c-success)" },
    canceled: { text: "Отменена", color: "var(--c-danger)" },
  };

  const paymentStatusLabel = {
    unpaid: { text: "Не оплачено", color: "var(--c-danger)" },
    partial: { text: "Частично", color: "var(--c-accent)" },
    paid: { text: "Оплачено", color: "var(--c-success)" },
  };

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button type="button" onClick={() => navigate("/wholesale")} style={styles.backBtn}>
          ← Клиенты
        </button>
      </div>

      {histStatus === "loading" && <Loading text="Загрузка…" />}
      {histStatus === "failed" && histError && <div style={styles.error}>{histError}</div>}

      {customer && (
        <div style={styles.customerCard}>
          <div style={styles.customerLeft}>
            <div style={styles.customerName}>
              {customer.name}
              {customer.is_active === false && <span style={styles.inactiveBadge}>Неактивен</span>}
            </div>
            {customer.phone && <div style={styles.customerMeta}>☎ {customer.phone}</div>}
            {customer.address && <div style={styles.customerMeta}>📍 {customer.address}</div>}
            <div style={styles.customerMeta}>
              Скидка по умолчанию:{" "}
              <strong>{formatDefaultDiscountLabel(customer)}</strong>
            </div>
          </div>
          <div style={styles.customerRight}>
            <div style={styles.debtLabel}>Текущий долг</div>
            <div style={totalDebt > 0 ? styles.debtAmount : styles.noDebtAmount}>
              {totalDebt > 0 ? `${totalDebt.toFixed(2)} сом` : "Без долга"}
            </div>
            {prepaymentBalance > 0 && (
              <div style={styles.prepaymentHint}>
                Предоплата на следующие покупки:{" "}
                <strong>{prepaymentBalance.toFixed(2)} сом</strong>
              </div>
            )}
            {totalDebt > 0 && (
              <button type="button" onClick={handleOpenPayDebtFromHeader} style={styles.payDebtHeaderBtn}>
                Погасить долг
              </button>
            )}
            {customer.is_active !== false && (
              <button type="button" onClick={handleOpenNewSale} style={styles.newSaleBtn}>
                + Новая продажа
              </button>
            )}
            {canDeleteCustomer && customer.is_active !== false && (
              <button
                type="button"
                onClick={handleDeleteCustomer}
                disabled={deleteLoading}
                style={styles.deleteHeaderBtn}
              >
                {deleteLoading ? "Удаляю…" : "Удалить клиента"}
              </button>
            )}
          </div>
        </div>
      )}

      {customer && (
        <div style={styles.discountCard}>
          <div style={styles.sectionTitle}>Индивидуальная скидка</div>
          <div style={styles.discountHint}>
            При создании новой продажи без своей скидки подставится эта (процент от суммы после скидок по
            строкам или фиксированная сумма в сомах).
          </div>
          {discountError && <div style={styles.error}>{discountError}</div>}
          {discountOk && <div style={styles.okBanner}>Сохранено</div>}
          <div style={styles.discountRow}>
            <div style={styles.field}>
              <label style={styles.label}>Тип</label>
              <Select
                value={discountType}
                onChange={(v) => { const t = v;
                  setDiscountType(t);
                  if (t === "none") setDiscountValue(""); }}
                options={[
                  { value: "none", label: "Без скидки" },
                  { value: "percent", label: "Процент (%)" },
                  { value: "amount", label: "Сумма (сом)" },
                ]}
                isSearchable={false}
              />
            </div>
            {(discountType === "percent" || discountType === "amount") && (
              <div style={styles.field}>
                <label style={styles.label}>{discountType === "percent" ? "Процент" : "Сумма"}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  max={discountType === "percent" ? "100" : undefined}
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  style={styles.input}
                />
              </div>
            )}
            <button
              type="button"
              style={styles.saveDiscountBtn}
              onClick={handleSaveDiscount}
              disabled={discountSaving}
            >
              {discountSaving ? "Сохранение..." : "Сохранить скидку"}
            </button>
          </div>
        </div>
      )}

      {histStatus === "succeeded" && (
        <>
          <div style={styles.section}>
            <div style={styles.sectionTitle}>История продаж ({sales.length})</div>
            {sales.length === 0 ? (
              <div style={styles.empty}>Нет продаж</div>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>№</th>
                      <th style={styles.th}>Дата</th>
                      <th style={styles.th}>Статус</th>
                      <th style={styles.th}>Оплата</th>
                      <th style={styles.th}>Сумма</th>
                      <th style={styles.th}>Оплачено</th>
                      <th style={styles.th}>Долг</th>
                      <th style={styles.th}>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((sale) => {
                      const st = statusLabel[sale.status] || { text: sale.status, color: "var(--c-text-muted)" };
                      const ps = paymentStatusLabel[sale.payment_status] || {
                        text: sale.payment_status,
                        color: "var(--c-text-muted)",
                      };
                      const debt = Number(sale.debt_amount) || 0;
                      return (
                        <tr key={sale.id} style={styles.tr} onClick={() => navigate(`/wholesale/sales/${sale.id}`)}>
                          <td style={styles.tdMono}>#{sale.id}</td>
                          <td style={styles.td}>
                            {sale.sale_dt ? new Date(sale.sale_dt).toLocaleString("ru-RU") : "—"}
                          </td>
                          <td style={styles.td}>
                            <span style={{ ...styles.badge, color: st.color, borderColor: st.color + "55", background: st.color + "18" }}>
                              {st.text}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <span style={{ ...styles.badge, color: ps.color, borderColor: ps.color + "55", background: ps.color + "18" }}>
                              {ps.text}
                            </span>
                          </td>
                          <td style={styles.td}>{Number(sale.grand_total || 0).toFixed(2)}</td>
                          <td style={styles.td}>{Number(sale.paid_amount || 0).toFixed(2)}</td>
                          <td style={styles.td}>
                            {debt > 0 ? (
                              <span style={styles.debtCell}>{debt.toFixed(2)}</span>
                            ) : (
                              <span style={styles.paidCell}>0.00</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            <div style={styles.actions}>
                              <button
                                type="button"
                                style={styles.viewBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/wholesale/sales/${sale.id}`);
                                }}
                              >
                                Открыть
                              </button>
                              {sale.status === "confirmed" && debt > 0 && (
                                <button
                                  type="button"
                                  style={styles.payBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPayment(sale, { prefillAmount: debt.toFixed(2) });
                                  }}
                                >
                                  Погасить долг
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>История платежей ({payments.length})</div>
            {payments.length === 0 ? (
              <div style={styles.empty}>Нет платежей</div>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Дата</th>
                      <th style={styles.th}>Продажа</th>
                      <th style={styles.th}>Сумма</th>
                      <th style={styles.th}>Тип</th>
                      <th style={styles.th}>Заметки</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} style={styles.trStatic}>
                        <td style={styles.td}>
                          {p.payment_dt ? new Date(p.payment_dt).toLocaleString("ru-RU") : "—"}
                        </td>
                        <td style={styles.tdMono}>#{p.sale_id || "—"}</td>
                        <td style={styles.td}>
                          <span style={styles.paidCell}>{Number(p.amount || 0).toFixed(2)} сом</span>
                        </td>
                        <td style={styles.td}>
                          {p.payment_type === "cash" ? "Наличные" : p.payment_type === "card" ? "Карта" : p.payment_type}
                        </td>
                        <td style={styles.td}>{p.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {newSaleModal && (
        <div style={styles.overlay} onMouseDown={() => setNewSaleModal(false)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Новая продажа</div>
              <button type="button" onClick={() => setNewSaleModal(false)} style={styles.modalClose}>
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              {saleError && <div style={styles.error}>{saleError}</div>}
              <div style={styles.field}>
                <label style={styles.label}>Склад *</label>
                {storesStatus === "loading" && <Loading text="Загрузка складов…" />}
                <Select
                  value={selectedStoreId}
                  onChange={setSelectedStoreId}
                  options={[
                    { value: "", label: "— Выберите склад —" },
                    ...stores.map((s) => ({
                      value: String(s.id),
                      label: s.name || `Склад #${s.id}`,
                    })),
                  ]}
                  isSearchable={false}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Заметки</label>
                <textarea
                  value={saleNotes}
                  onChange={(e) => setSaleNotes(e.target.value)}
                  placeholder="Дополнительная информация..."
                  style={styles.textarea}
                  rows={2}
                />
              </div>
              <div style={styles.modalActions}>
                <button type="button" onClick={handleCreateSale} style={styles.confirmBtn} disabled={saleLoading}>
                  {saleLoading ? "Создание..." : "Создать продажу"}
                </button>
                <button
                  type="button"
                  onClick={() => setNewSaleModal(false)}
                  style={styles.cancelBtn}
                  disabled={saleLoading}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {paymentModal && (
        <div style={styles.overlay} onMouseDown={() => setPaymentModal(null)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Погашение долга — продажа #{paymentModal.id}</div>
              <button type="button" onClick={() => setPaymentModal(null)} style={styles.modalClose}>
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              {paymentError && <div style={styles.error}>{paymentError}</div>}
              <div style={styles.infoBox}>
                {salesWithDebt.length > 1 && (
                  <div style={styles.infoHint}>
                    Долг по нескольким продажам: платёж привязывается к одной продаже. После сохранения
                    можно снова нажать «Погасить долг» для следующей.
                  </div>
                )}
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Всего долга у клиента:</span>
                  <span style={styles.infoValue}>{totalDebt.toFixed(2)} сом</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Сумма продажи:</span>
                  <span style={styles.infoValue}>{Number(paymentModal.grand_total || 0).toFixed(2)} сом</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Уже оплачено:</span>
                  <span style={styles.infoValue}>{Number(paymentModal.paid_amount || 0).toFixed(2)} сом</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Остаток долга:</span>
                  <span style={{ ...styles.infoValue, color: "var(--c-danger)", fontWeight: 900 }}>
                    {Number(paymentModal.debt_amount || 0).toFixed(2)} сом
                  </span>
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Сумма оплаты *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                  style={styles.input}
                  autoFocus
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Тип оплаты</label>
                <Select
                  value={paymentType}
                  onChange={setPaymentType}
                  options={[
                    { value: "cash", label: "Наличные" },
                    { value: "card", label: "Карта" },
                    { value: "online", label: "Перевод" },
                  ]}
                  isSearchable={false}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Заметки</label>
                <input
                  type="text"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Необязательно"
                  style={styles.input}
                />
              </div>
              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={handleCreatePayment}
                  style={styles.confirmBtn}
                  disabled={paymentLoading}
                >
                  {paymentLoading ? "Сохранение..." : "Зафиксировать оплату"}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentModal(null)}
                  style={styles.cancelBtn}
                  disabled={paymentLoading}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    padding: "20px 28px",
    background: "var(--c-bg)",
    minHeight: "100%",
    color: "var(--c-text)",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  topBar: { display: "flex", alignItems: "center", gap: 12 },
  backBtn: {
    padding: "8px 16px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
  },
  notice: { padding: 16, textAlign: "center", opacity: 0.75, fontSize: 16 },
  error: {
    padding: "12px 16px",
    background: "rgba(248,113,113,0.1)",
    border: "1px solid rgba(248,113,113,0.2)",
    borderRadius: 12,
    color: "var(--c-danger)",
    fontSize: 15,
  },
  customerCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 20,
    padding: "24px 28px",
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
  },
  customerLeft: { display: "flex", flexDirection: "column", gap: 6 },
  customerName: { fontSize: 23, fontWeight: 900, color: "var(--c-text)" },
  inactiveBadge: { marginLeft: 10, fontSize: 14, fontWeight: 700, color: "var(--c-text-muted)", padding: "2px 8px", borderRadius: 99, background: "rgba(148,163,184,0.14)", border: "1px solid rgba(148,163,184,0.25)", verticalAlign: "middle" },
  customerMeta: { fontSize: 15, opacity: 0.80, lineHeight: 1.5 },
  customerRight: { display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" },
  debtLabel: { fontSize: 14, fontWeight: 900, color: "var(--c-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" },
  debtAmount: { fontSize: 23, fontWeight: 900, color: "var(--c-danger)" },
  prepaymentHint: {
    marginTop: 8,
    fontSize: 15,
    fontWeight: 600,
    color: "var(--c-success)",
    maxWidth: 220,
    textAlign: "right",
    lineHeight: 1.35,
  },
  noDebtAmount: { fontSize: 23, fontWeight: 900, color: "var(--c-success)" },
  payDebtHeaderBtn: {
    marginTop: 4,
    padding: "10px 20px",
    borderRadius: 99,
    border: "1px solid rgba(251,113,133,0.45)",
    background: "rgba(251,113,133,0.12)",
    color: "var(--c-danger)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
    whiteSpace: "nowrap",
  },
  newSaleBtn: {
    marginTop: 6,
    padding: "10px 20px",
    borderRadius: 99,
    border: "1px solid color-mix(in srgb, var(--c-success) 30%, transparent)",
    background: "color-mix(in srgb, var(--c-success) 14%, transparent)",
    color: "var(--c-success)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
    whiteSpace: "nowrap",
  },
  deleteHeaderBtn: {
    marginTop: 6,
    padding: "10px 20px",
    borderRadius: 99,
    border: "1px solid rgba(248,113,113,0.35)",
    background: "transparent",
    color: "var(--c-danger)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
    whiteSpace: "nowrap",
  },
  section: { display: "flex", flexDirection: "column", gap: 12 },
  sectionTitle: { fontSize: 18, fontWeight: 900, color: "var(--c-text)" },
  empty: { padding: 32, textAlign: "center", opacity: 0.65, fontSize: 15 },
  tableWrap: {
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    overflow: "hidden",
    background: "var(--c-card)",
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 900,
    color: "var(--c-text-sub)",
    background: "var(--c-input)",
    borderBottom: "1px solid var(--c-border)",
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  tr: { cursor: "pointer" },
  trStatic: {},
  td: {
    padding: "12px 16px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    verticalAlign: "middle",
  },
  tdMono: {
    padding: "12px 16px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    fontFamily: "monospace",
  },
  badge: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 99,
    fontSize: 13,
    fontWeight: 900,
    border: "1px solid",
  },
  debtCell: { color: "var(--c-danger)", fontWeight: 900 },
  paidCell: { color: "var(--c-success)", fontWeight: 700 },
  actions: { display: "flex", gap: 8, flexWrap: "wrap" },
  viewBtn: {
    padding: "5px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 13,
  },
  payBtn: {
    padding: "5px 12px",
    borderRadius: 99,
    border: "1px solid rgba(245,197,24,0.3)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 50,
  },
  modal: {
    width: "min(460px, 90vw)",
    maxHeight: "90vh",
    background: "var(--c-bg)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalTitle: { fontSize: 18, fontWeight: 900, color: "var(--c-text)" },
  modalClose: {
    width: 34,
    height: 34,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBody: {
    padding: 20,
    overflow: "auto",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  infoBox: {
    padding: "14px 16px",
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  infoRow: { display: "flex", justifyContent: "space-between", fontSize: 15 },
  infoHint: {
    fontSize: 14,
    lineHeight: 1.45,
    color: "var(--c-text-muted)",
    marginBottom: 4,
    paddingBottom: 8,
    borderBottom: "1px solid var(--c-border)",
  },
  infoLabel: { opacity: 0.75, fontWeight: 700 },
  infoValue: { fontWeight: 900 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 15, fontWeight: 700, color: "var(--c-text-sub)" },
  input: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
  },
  select: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
    cursor: "pointer",
  },
  textarea: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
    resize: "vertical",
    fontFamily: "inherit",
  },
  modalActions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 },
  confirmBtn: {
    padding: "11px 22px",
    borderRadius: 99,
    border: "1px solid color-mix(in srgb, var(--c-success) 30%, transparent)",
    background: "color-mix(in srgb, var(--c-success) 14%, transparent)",
    color: "var(--c-success)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 16,
  },
  cancelBtn: {
    padding: "11px 22px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 16,
  },
  discountCard: {
    padding: "20px 24px",
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  discountHint: { fontSize: 14, color: "var(--c-text-muted)", lineHeight: 1.45 },
  discountRow: { display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" },
  saveDiscountBtn: {
    padding: "10px 18px",
    borderRadius: 99,
    border: "1px solid rgba(245,197,24,0.35)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
    marginBottom: 2,
  },
  okBanner: {
    padding: "8px 12px",
    borderRadius: 8,
    background: "rgba(74,222,128,0.12)",
    border: "1px solid rgba(74,222,128,0.25)",
    color: "var(--c-success)",
    fontSize: 15,
    fontWeight: 700,
  },
};

function formatDefaultDiscountLabel(c) {
  const t = c?.default_discount_type;
  if (!t || t === "none") return "нет";
  const n = Number(c?.default_discount_value);
  if (Number.isNaN(n)) return "—";
  if (t === "percent") return `${n}%`;
  if (t === "amount") return `${n.toFixed(2)} сом`;
  return "—";
}
